import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { dismissQuoteRequest, getDismissedQuoteRequestPage } from '../api/adminApi';
import { logError, toNullableLocaleDateTimeString } from '../utilities';
import ConfirmModal, { ButtonLoadingSpinner, ElementWithKey } from './ConfirmModal';
import GenericDataTable from './GenericDataTable';
import { QuoteRequestResponse } from '../apiModels/quoteRequestResponse';
import { quoteContactMethods } from '../apiModels/quoteContactMethod';
import QuoteRequestDismissButton from './QuoteRequestDismissButton';
import QuoteRequestDetailsModal from './QuoteRequestDetailsModal';

const pageSize = 10;

const TableRowFormatter = (props: { quote: QuoteRequestResponse, setDetailsSelectedQuote: (quoteRequest: QuoteRequestResponse | null) => void, setShowQuoteDetailsModal: (isShown: boolean) => void }) => {
    const quote = props.quote;

    const preferredContactMethod = quote.contactMethod === quoteContactMethods.email ? 'Email' : 'Phone';
    const shipmentDetailsPreview = quote.shipmentDetails.length > 20 ? quote.shipmentDetails.substring(0, 20) + '...' : quote.shipmentDetails;

    return (
        <tr key={quote.quoteRequestId}>
            <td>
                <button className="btn btn-sm btn-outline-primary" onClick={() => {
                    props.setDetailsSelectedQuote(quote);
                    props.setShowQuoteDetailsModal(true);
                }}>
                    <i className="bi bi-search"></i>
                </button>
            </td>
            <td>{quote.firstName}</td>
            <td>{quote.lastName}</td>
            <td>{preferredContactMethod}</td>
            <td>{shipmentDetailsPreview}</td>
            <td>{toNullableLocaleDateTimeString(quote.createdDate)}</td>
            <td>{toNullableLocaleDateTimeString(quote.dismissedDate)}</td>
        </tr>
    );
};

export default function QuotesDismissedListTable() {
    const [ isDataTableLoading, setIsDataTableLoading ] = useState<boolean>(false);
    const [ quoteRequests, setQuoteRequests ] = useState<QuoteRequestResponse[]>([]);
    const [ selectedPage, setSelectedPage ] = useState<number>(0);
    const notifyUserOfError = (err: string) => toast(err, { type: 'error' });
    
    const [detailsSelectedQuote, setDetailsSelectedQuote] = useState<QuoteRequestResponse | null>(null);
    const [showQuoteDetailsModal, setShowQuoteDetailsModal] = useState<boolean>(false);

    const loadDismissedQuoteRequests = async() => {
        setIsDataTableLoading(true);
        
        try {
            let quotes = await getDismissedQuoteRequestPage(selectedPage, pageSize);
            setQuoteRequests(quotes);
            setIsDataTableLoading(false);
        } 
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }
    
            logError(error);
            setIsDataTableLoading(false);
        }
    };
    
    useEffect(() => {
        loadDismissedQuoteRequests();
    }, []);

    return (
        <>
            <div className='row'>
                <h3 className='col-9'>Dismissed Quote Requests</h3>
                
                <div className='col-2 text-end'>
                    {(quoteRequests.length < pageSize && selectedPage === 0) ||
                        <div className='col-12 text-center'>
                            <button className='btn btn-default' hidden={selectedPage === 0} onClick={() => setSelectedPage(selectedPage - 1)} disabled={selectedPage === 0}>
                                <i className='bi bi-chevron-left fs-4 fw-bold'></i>
                            </button>
                            <span className='mx-2'>{selectedPage + 1}</span>
                            <button className='btn btn-default' hidden={quoteRequests.length < pageSize} onClick={() => setSelectedPage(selectedPage + 1)} disabled={quoteRequests.length < pageSize}>
                                <i className='bi bi-chevron-right fs-4 fw-bold'></i>
                            </button>
                        </div>
                    }
                </div>

                <div className='col-1 text-end'>
                    <button className='btn btn-default' onClick={() => loadDismissedQuoteRequests()}>
                        <i className='bi bi-arrow-repeat fs-4 fw-bold'></i>
                    </button>
                </div>
            </div>

            <GenericDataTable data={quoteRequests} 
                dataFormatter={data => <>{data.map(d => <TableRowFormatter key={d.quoteRequestId} quote={d} setDetailsSelectedQuote={setDetailsSelectedQuote} setShowQuoteDetailsModal={setShowQuoteDetailsModal} />)}</>}
                noDataMessage='There are no dismissed quote requests.'
                columnCount={7}
                isLoading={isDataTableLoading}>
                <tr>
                    <td></td>
                    <td>First Name</td>
                    <td>Last Name</td>
                    <td>Preferred Contact Method</td>
                    <td>Details</td>
                    <td>Created</td>
                    <td>Dismissed Date</td>
                </tr>
            </GenericDataTable>
            <ToastContainer />
            {detailsSelectedQuote != null &&
            <QuoteRequestDetailsModal
                show={showQuoteDetailsModal} 
                setShow={setShowQuoteDetailsModal}
                quoteRequest={detailsSelectedQuote} />
            }
        </>
    );
}
